/**
 * HomeView (Intelligence Studio)
 *
 * EXACT JSX extracted from containers/IntelligenceStudioPage.jsx HOME VIEW.
 * This is presentational-only; state/handlers live in the container.
 */

import React from 'react';

import { Loader2, Plus, Search } from 'lucide-react';

import { CreateNotebookModal, DeleteConfirmationModal, NotebookCard, Toast } from '@/pages/intelligence-studio/components';

const HomeView = ({
  notebooks,
  filteredNotebooks,
  isLoading,
  searchQuery,
  setSearchQuery,

  showCreateModal,
  setShowCreateModal,
  handleCreateNotebook,

  deleteConfirmation,
  confirmDelete,
  closeDeleteConfirmation,

  error,
  setError,

  handleSelectNotebook,
  handleDeleteNotebook,
  handleRenameNotebook,
}) => {
  const sourcesCount = notebooks.reduce((acc, n) => acc + (n.files?.length || 0), 0);
  const studiosWithSources = notebooks.filter(n => (n.files?.length || 0) > 0).length;
  const draftCount = Math.max(0, notebooks.length - studiosWithSources);
  return (
    <div className="h-full w-full flex flex-col bg-white dark:bg-neutral-900 overflow-hidden">
      {/* Home Header - FIXED (flex-shrink-0) */}
      <div className="flex-shrink-0 px-6 py-5 border-b border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
        <div className="w-full flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12">
              <img
                src="/images/intelligence-studio-logo.png"
                alt="Intelligence Studio"
                className="h-full w-full object-contain"
                loading="eager"
                draggable={false}
              />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-white whitespace-nowrap">
              Intelligence Studio
            </h1>
          </div>

          <div className="flex-1 mx-4 lg:mx-6 min-w-0">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search studios..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-100 dark:bg-neutral-800 border border-slate-300 dark:border-neutral-600 rounded-xl text-sm focus:ring-2 focus:ring-slate-900/20 dark:focus:ring-slate-100/20 focus:border-slate-900 dark:focus:border-slate-100"
              />
            </div>
          </div>

          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="text-sm text-slate-500 dark:text-neutral-400">
              {studiosWithSources} studio{studiosWithSources !== 1 ? 's' : ''} • {sourcesCount} source{sourcesCount !== 1 ? 's' : ''}
              {draftCount > 0 ? ` • ${draftCount} draft${draftCount !== 1 ? 's' : ''}` : ''}
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 px-5 py-2.5 text-white rounded-xl transition-colors shadow-lg shadow-slate-900/20 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-950 hover:from-slate-900 hover:via-slate-800 hover:to-black"
            >
              <Plus className="h-5 w-5" />
              <span className="font-medium">New Studio</span>
            </button>
          </div>
        </div>
      </div>

      {/* Studios Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="w-full">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-900 dark:text-slate-100" />
            </div>
          ) : filteredNotebooks.length === 0 ? (
            <div className="text-center py-20">
              <Search className="h-12 w-12 mx-auto text-slate-300 dark:text-neutral-600 mb-4" />
              <p className="text-slate-500 dark:text-neutral-400">
                No studios found matching "{searchQuery}"
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredNotebooks.map(notebook => (
                <NotebookCard
                  key={notebook.id}
                  notebook={notebook}
                  onSelect={() => handleSelectNotebook(notebook)}
                  onDelete={() => handleDeleteNotebook(notebook)}
                  onRename={handleRenameNotebook}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateNotebookModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateNotebook}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.open && (
        <DeleteConfirmationModal
          notebookName={deleteConfirmation.notebookName}
          onConfirm={confirmDelete}
          onCancel={closeDeleteConfirmation}
        />
      )}

      {/* Error toast */}
      {error && <Toast message={error} onClose={() => setError(null)} />}
    </div>
  );
};

export default HomeView;
